import React, { useState, useEffect, useCallback } from "react";
import {
  addAIModelMaster,
  updateAIModelMaster,
  getAIModelMasterById,
} from "../../services/aiModelMastersService";
import { validateAIModelMaster } from "../../utils/validation";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { handleErrors } from "../../utils/errorHandler";
import ComponentHeader from "../Common/OtherElements/ComponentHeader";

const INITIAL_FORM_STATE = {
  providerType: "",
  modelName: "",
  isDefault: false,
};

const INITIAL_ERRORS_STATE = {
  providerType: "",
  modelName: "",
};

const mapAIModelResponse = (data) => ({
  providerType: data.providerType || data.ProviderType || "",
  modelName: data.modelName || data.ModelName || "",
  isDefault: Boolean(data.isDefault ?? data.IsDefault ?? false),
});

export const AddAIModel = ({
  editMode = false,
  initialData = {},
  onSuccess,
  setSelectedAIModel,
  setEditMode,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState(INITIAL_ERRORS_STATE);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const modelId = initialData?.id ?? initialData?.Id;
      if (editMode && modelId) {
        try {
          const data = await getAIModelMasterById(modelId);
          setFormData(mapAIModelResponse(data));
        } catch (error) {
          handleErrors(error);
        }
      } else {
        setFormData(INITIAL_FORM_STATE);
      }
    };
    fetchData();
  }, [editMode, initialData]);

  const handleInputChange = ({ target: { name, value } }) => {
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleIsDefaultChange = ({ target: { checked } }) => {
    setFormData((prevData) => ({ ...prevData, isDefault: checked }));
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_STATE);
    setErrors(INITIAL_ERRORS_STATE);
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { valid, errors: validationErrors } = validateAIModelMaster(formData);
      setErrors(validationErrors);

      if (!valid) return;

      const modelId = initialData?.id ?? initialData?.Id;
      const payload = {
        providerType: formData.providerType.trim(),
        modelName: formData.modelName.trim(),
        isDefault: Boolean(formData.isDefault),
      };

      if (editMode) {
        payload.id = modelId;
      }

      try {
        setIsButtonDisabled(true);
        const action = editMode ? updateAIModelMaster : addAIModelMaster;
        const response = await action(payload);

        if (response?.data?.isSuccess === false) {
          toast.error(
            response.data.message || "Request failed. Please try again."
          );
          return;
        }

        toast.success(
          `AI model ${editMode ? "updated" : "added"} successfully!`
        );
        resetForm();
        if (onSuccess) {
          onSuccess();
        }
        setEditMode(false);
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsButtonDisabled(false);
      }
    },
    [formData, editMode, initialData, onSuccess, setEditMode]
  );

  const handleAddNewClick = () => {
    resetForm();
    setSelectedAIModel(null);
    setEditMode(false);
  };

  return (
    <>
      <ComponentHeader title="AI Model Masters" />

      <div className="row">
        <div className="col-xxl-12">
          <div className="card mt-xxl-n5">
            <div className="card-header">
              <h5 className="mb-sm-1 mt-sm-1">
                {editMode ? "Update AI Model" : "Add AI Model"}
              </h5>
            </div>

            <div className="card-body p-4">
              <form onSubmit={handleSubmit} method="POST">
                <div className="row">
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="mb-3">
                      <label htmlFor="providerType" className="form-label">
                        Provider Type{" "}
                        <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        name="providerType"
                        id="providerType"
                        placeholder="Enter Provider Type"
                        value={formData.providerType}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.providerType ? "is-invalid" : ""
                        }`}
                      />
                      {errors.providerType && (
                        <div className="invalid-feedback">
                          {errors.providerType}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="mb-3">
                      <label htmlFor="modelName" className="form-label">
                        Model Name <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        name="modelName"
                        id="modelName"
                        placeholder="Enter Model Name"
                        value={formData.modelName}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.modelName ? "is-invalid" : ""
                        }`}
                      />
                      {errors.modelName && (
                        <div className="invalid-feedback">
                          {errors.modelName}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="mb-3">
                      <label className="form-label d-block">Is Default</label>
                      <div className="form-check form-switch form-switch-custom form-switch-primary">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="isDefault"
                          name="isDefault"
                          checked={formData.isDefault}
                          onChange={handleIsDefaultChange}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="isDefault"
                        >
                          Set as default model
                        </label>
                      </div>
                      {formData.isDefault && (
                        <small className="text-warning d-block mt-2">
                          Setting this model as default may override existing
                          default model.
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-12">
                    <div>
                      <button
                        type="submit"
                        className="btn btn-secondary pt-1 pb-1 p-3"
                        disabled={isButtonDisabled}
                      >
                        {isButtonDisabled
                          ? editMode
                            ? "Updating"
                            : "Saving"
                          : editMode
                          ? "Update"
                          : "Save"}
                      </button>
                      {editMode && (
                        <button
                          type="button"
                          onClick={handleAddNewClick}
                          className="btn btn-danger ms-1 pt-1 pb-1 p-3"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
